<svelte:options customElement="hb-toast" />

<script lang="ts">
  import type { Component, TToastButton } from "./types/webcomponent.type";

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  import { fade } from "svelte/transition";
  import { quintOut } from "svelte/easing";

  let {
    id = "",
    // style,
    show = "no",
    content = "",
    small,
    title = "",
    img = "",
    progress = "",
    timeout,
    level = "info",
    position = "bottom-right",
    buttons = [],
  }: Component = $props();

  // State variables for fadeout and timeout
  let isVisible = $state(false);
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  // State variables for elapsed time display
  let timeIntervalId: ReturnType<typeof setInterval> | null = null;
  let elapsedSeconds = $state(0);
  let startTime = $state<number | null>(null);

  // Progress bar tracking - independent from timer
  let progressStartTime = $state<number | null>(null);
  let progressTrigger = $state(0);
  let progressIntervalId: ReturnType<typeof setInterval> | null = null;

  // Parse numeric progress when provided as attribute (0-100)
  const progressNumeric = $derived.by(() => {
    if (progress === "" || progress === undefined || progress === null)
      return null;
    const n = Number(progress);
    if (Number.isNaN(n)) return null;
    return Math.max(0, Math.min(100, n));
  });

  // Derived values using $derived for reactive calculations
  const progressPercentage = $derived.by(() => {
    // If external progress is provided, use it directly
    if (progressNumeric !== null) return progressNumeric;

    // Reference trigger to animate during timeout mode
    const _ = progressTrigger;

    if (!timeout || Number(timeout) <= 0 || !progressStartTime) return 0;

    const now = Date.now();
    const elapsed = now - progressStartTime; // elapsed in milliseconds
    const timeoutMs = Number(timeout); // timeout is in milliseconds
    const p = (elapsed / timeoutMs) * 100;

    return Math.min(p, 100);
  });

  function formatRemaining(secondsTotal: number) {
    if (secondsTotal <= 0) return "now";

    const days = Math.floor(secondsTotal / 86400);
    const hours = Math.floor((secondsTotal % 86400) / 3600);
    const minutes = Math.floor((secondsTotal % 3600) / 60);
    const seconds = secondsTotal % 60;

    const parts: string[] = [];

    if (secondsTotal >= 86400) {
      // days, hours, minutes, seconds
      parts.push(`${days} ${days === 1 ? "day" : "days"}`);
      parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
      parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
    } else if (secondsTotal >= 3600) {
      // hours, minutes, seconds
      parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
      parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
    } else if (secondsTotal >= 60) {
      // minutes, seconds
      parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
    } else {
      // seconds only
      parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
    }

    return parts.join(" ") + " remaining";
  }

  const formattedTime = $derived.by(() => {
    // Reference elapsedSeconds so this recomputes every second
    const _tick = elapsedSeconds;

    // When `small` is not provided and external progress is given, show percentage
    if (!small && progressNumeric !== null) {
      return `${progressPercentage}%`;
    }

    // When `small` is not provided, show remaining time (if timeout is set)
    if (!small && timeout && Number(timeout) > 0) {
      const timeoutSeconds = Math.floor(Number(timeout) / 1000);
      const remainingSeconds = Math.max(0, timeoutSeconds - elapsedSeconds);
      return formatRemaining(remainingSeconds);
    }

    // Fallback: elapsed time "ago"
    if (elapsedSeconds === 0) return "now";
    if (elapsedSeconds === 1) return "1 second ago";
    return `${elapsedSeconds} seconds ago`;
  });

  /** Text for the status line (timeout, %, elapsed, or custom `small`). Mirrors default `header_small` slot. */
  const headerMetaText = $derived.by(() => {
    const s = small;
    if (s) return String(s);
    if (progressNumeric !== null) return `${progressPercentage}%`;
    if (s !== "") return formattedTime;
    return "";
  });

  /** Bootstrap Icons class for the status line (context: manual label, progress, countdown, clock). */
  const statusIconClass = $derived.by(() => {
    if (small) return "bi-tag";
    if (progressNumeric !== null) return "bi-reception-4";
    if (timeout && Number(timeout) > 0) return "bi-hourglass-split";
    return "bi-clock-history";
  });

  const levelColorClass = $derived.by(() => {
    const colorMap: Record<string, string> = {
      primary: "has-background-primary",
      secondary: "has-background-link",
      success: "has-background-success",
      danger: "has-background-danger",
      warning: "has-background-warning",
      info: "has-background-info",
      light: "has-background-light",
      dark: "has-background-dark",
    };
    return colorMap[level] || "has-background-info";
  });

  /** Bulma notification color + light tint (semantic `level`). */
  const levelNotificationClass = $derived.by(() => {
    const map: Record<string, string> = {
      primary: "is-primary is-light",
      secondary: "is-link is-light",
      success: "is-success is-light",
      danger: "is-danger is-light",
      warning: "is-warning is-light",
      info: "is-info is-light",
      light: "is-light",
      dark: "is-dark is-light",
    };
    return map[level] ?? "is-info is-light";
  });

  const positionClass = $derived.by(() => {
    const positionMap: Record<string, string> = {
      "top-left": "hb-toast--top-left",
      "top-center": "hb-toast--top-center",
      "top-right": "hb-toast--top-right",
      "bottom-left": "hb-toast--bottom-left",
      "bottom-center": "hb-toast--bottom-center",
      "bottom-right": "hb-toast--bottom-right",
    };
    return positionMap[position] || positionMap["bottom-right"];
  });

  // Function to update elapsed time for display
  function updateElapsedTime() {
    if (startTime) {
      const now = Date.now();
      elapsedSeconds = Math.floor((now - startTime) / 1000);
    }
  }

  // Function to start elapsed time tracking (for display only)
  function startElapsedTimeTracking() {
    if (!small && progressNumeric === null) {
      startTime = Date.now();
      elapsedSeconds = 0;
      timeIntervalId = setInterval(updateElapsedTime, 1000) as any;
    }
  }

  // Function to stop elapsed time tracking
  function stopElapsedTimeTracking() {
    if (timeIntervalId) {
      clearInterval(timeIntervalId);
      timeIntervalId = null;
      elapsedSeconds = 0;
      startTime = null;
    }
  }

  // Function to start progress bar tracking
  function startProgressTracking() {
    if (progressNumeric === null && timeout && Number(timeout) > 0) {
      // Clear any existing interval
      if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }

      progressStartTime = Date.now();
      progressTrigger = 0;

      // Start an interval to update the progress trigger every 100ms for smooth animation
      progressIntervalId = setInterval(() => {
        if (progressStartTime) {
          progressTrigger = Date.now();
        }
      }, 100) as any;
    }
  }

  // Function to stop progress bar tracking
  function stopProgressTracking() {
    if (progressIntervalId) {
      clearInterval(progressIntervalId);
      progressIntervalId = null;
    }
    progressStartTime = null;
    progressTrigger = 0;
  }

  // Function to clean up all timers
  function cleanup() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    stopElapsedTimeTracking();
    stopProgressTracking();
  }

  // const onOpened = () => dispatch("toastShow", { id, show: true });
  function close() {
    cleanup();
    show = "no";
    dispatch("changeVisibility", { id, show: false });
  }

  function autoClose() {
    isVisible = false;
    // Don't stop timer immediately, wait for fadeout to complete
    setTimeout(() => {
      show = "no";
      cleanup(); // Clean up all timers after fadeout
      dispatch("changeVisibility", { id, show: false, disappear: true });
    }, 400); // After out:fade duration + buffer
  }

  function handleAction(btn: TToastButton) {
    dispatch("action", {
      id,
      action: btn.action,
    });
    close();
  }

  const parsedButtons = $derived.by(() => {
    let btns = buttons;
    if (typeof btns === "string") {
      try {
        btns = JSON.parse(btns);
      } catch (e) {
        btns = [];
      }
    }
    if (!Array.isArray(btns)) return [];

    return btns.map((btn) => {
      const defaults =
        btn.type === "confirm"
          ? {
              text: "Confirm",
              themeColor: "success" as const,
              icon: "bi-check-lg",
            }
          : {
              text: "Cancel",
              themeColor: "secondary" as const,
              icon: "bi-x-lg",
            };

      return {
        ...defaults,
        ...btn,
      };
    });
  });

  // Handle timeout when component becomes visible
  $effect(() => {
    if (show === "yes") {
      isVisible = true;

      // Start elapsed time tracking if small is not defined
      startElapsedTimeTracking();

      // Start progress bar tracking only in timeout mode
      startProgressTracking();

      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout if provided
      if (progressNumeric === null && timeout && Number(timeout) > 0) {
        timeoutId = setTimeout(() => {
          autoClose();
        }, Number(timeout)) as any;
      }
    } else {
      isVisible = false;
      cleanup();
    }
  });

  // Auto close when external progress reaches 100%
  $effect(() => {
    if (
      show === "yes" &&
      progressNumeric !== null &&
      progressPercentage >= 100
    ) {
      autoClose();
    }
  });
  // let backdrop = true;
  // let ignoreBackdrop = false;
  // let keyboard = true;

  // let _keyboardEvent;
  // function attachEvent(target, ...args) {
  // 	target.addEventListener(...args);
  // 	return {
  // 		remove: () => target.removeEventListener(...args),
  // 	};
  // }
  // function checkClass(className) {
  // 	return document.body.classList.contains(className);
  // }
  // function toastOpen() {
  // 	if (!checkClass("toast-open")) {
  // 		document.body.classList.add("toast-open");
  // 	}
  // }
  // function toastClose() {
  // 	if (checkClass("toast-open")) {
  // 		document.body.classList.remove("toast-open");
  // 	}
  // }
  // function handleBackdrop(event) {
  // 	if (backdrop && !ignoreBackdrop) {
  // 		event.stopPropagation();
  // 		show = false;
  // 	}
  // }
  // function onToastOpened() {
  // 	if (keyboard) {
  // 		_keyboardEvent = attachEvent(document, "keydown", (e) => {
  // 			if ((event as any).key === "Escape") {
  // 				show = false;
  // 				onClosed();
  // 			}
  // 		});
  // 	}
  // 	onOpened();
  // }
  // function onToastClosed() {
  // 	if (_keyboardEvent) {
  // 		_keyboardEvent.remove();
  // 	}
  // 	onClosed();
  // }
  // Watching changes for Open vairable
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://cdn.jsdelivr.net/npm/bootstrap-icons@latest/font/bootstrap-icons.css"
  />
</svelte:head>
{#if show === "yes"}
  <div
    part="toast"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
    class="notification hb-toast {levelNotificationClass} {positionClass}{parsedButtons.length === 0
      ? ' hb-toast--no-actions'
      : ''}"
    id="toast"
    in:fade={{ duration: 380, easing: quintOut }}
    out:fade={{ duration: 280, easing: quintOut }}
  >
    <button
      type="button"
      class="delete"
      aria-label="Close"
      onclick={close}
    ></button>
    <div class="hb-toast__body">
      <div class="hb-toast__icon-anchor">
        <span class="icon is-medium hb-toast__icon">
          <slot name="header_icon"
            ><i class="bi bi-bell" aria-hidden="true"></i></slot
          >
        </span>
      </div>
      <div class="hb-toast__main is-flex-grow-1 is-flex-shrink-1">
        <div class="hb-toast__header-row">
          <p class="title is-6 mb-0 hb-toast__title">
            <slot name="header_strong" />{title}
          </p>
        </div>

        {#if headerMetaText}
          <div class="hb-toast__status" part="status">
            <span
              class="tag is-light is-rounded is-size-7 hb-toast__status-chip"
            >
              <i
                class="bi {statusIconClass} hb-toast__status-icon"
                aria-hidden="true"
              ></i>
              <span class="hb-toast__status-text">
                <slot name="header_small">{headerMetaText}</slot>
              </span>
            </span>
          </div>
        {/if}

        <div class="content mb-0 hb-toast__content">
          <slot name="body">{content}</slot>
        </div>
      </div>
    </div>

    {#if parsedButtons && parsedButtons.length > 0}
      <div
        class="buttons are-small is-justify-content-flex-end hb-toast__actions"
      >
        {#each parsedButtons as btn (btn.action)}
          <button
            type="button"
            class={`button ${btn.themeColor === "secondary" ? "is-light" : `is-${btn.themeColor}`}`}
            style={btn.color
              ? `background-color: ${btn.color}; border-color: ${btn.color}; color: #fff;`
              : ""}
            onclick={() => handleAction(btn)}
          >
            {#if btn.icon}
              <span class="icon is-small"><i class="bi {btn.icon}"></i></span>
            {/if}
            <span>{btn.text}</span>
          </button>
        {/each}
      </div>
    {/if}

    {#if progressNumeric !== null || (timeout && Number(timeout) > 0)}
      <div class="toast-progress-container">
        <div
          class="toast-progress-bar {levelColorClass}"
          style="width: {progressPercentage}%; transition: width 0.12s linear;"
        ></div>
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
